import { Dialog, Select } from "@rio-cloud/rio-uikit";
import SearchAutocomplete from "../../../../../../form/searchAutocomplete";
import useAssets from "../../../../../../../hooks/useAssets";
import { Asset } from "../../../../../../../models/asset";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ErrorDialog from "../../../../../../dialogs/ErrorDialog";
import { createTrip } from "../../../../../../../api/routing";
import SuccessDialog from "../../../../../../dialogs/SuccessDialog";
import { Driver } from "../../../../../../../models/driver";
import { assignDriverToOccurrenceDispatch } from "../../../../../../../api/occurrence";
import { Occurrence, OccurrenceDispatch } from "../../../../../../../models/occurrence";
import { fetchUserId } from "../../../../../../../api/fetchUser";
import { useSelector } from "react-redux";
import { getUserProfile } from "../../../../../../../configuration/login/loginSlice";

interface CreateTripDialogProps {
    routeId: string;
    show: boolean;
    onClose: () => void;
    onTripCreated: (driverName: string) => void;
    drivers: Driver[];
    occurrence: Occurrence | null;
    occurrenceDispatch: OccurrenceDispatch;
}

const CreateTripDialog = (props: CreateTripDialogProps) => {
    const intl = useIntl();
    const { getFilteredAssets } = useAssets();
    const userProfile = useSelector(getUserProfile);
    const [loading, setLoading] = useState<boolean>(false);
    const [form, setForm] = useState<{
        assetId: string;
        assetName: string;
        driverId: string;
        driverName: string;
        profileId: string;
    }>({
        assetId: '',
        assetName: '',
        driverId: '',
        driverName: '',
        profileId: '',
    });

    const [errorDialog, setErrorDialog] = useState<string | null>(null);
    const [successDialog, setSuccessDialog] = useState<string | null>(null);

    useEffect(() => {
        if (props.show) {
            setForm({
                assetId: '',
                assetName: '',
                driverId: '',
                driverName: '',
                profileId: '',
            });
        }
    }, [props.show]);

    
    const handleCreateTrip = async () => {
        const userId = await fetchUserId(userProfile);
        if (!userId) {
            setErrorDialog(intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.fetchUserError' }));
            return;
        }

        setLoading(true);
        try {
            const date = new Date();
            const formattedDate = date.toISOString().replace(/\.\d{3}Z$/, '+00:00');
            const { wasCreated, tripId, error, errorId } = await createTrip(props.routeId, userId, form.driverId, form.driverName, form.assetId, form.assetName, formattedDate, form.profileId);
            if (!wasCreated) {
                setErrorDialog(errorId ? intl.formatMessage({ id: errorId }) : error ?? intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.createTripError' }));
            } else {
                await assignDriverToOccurrenceDispatch(props.occurrence?.id ?? '', props.occurrenceDispatch.id ?? '', form.driverName);
                setSuccessDialog('Viagem criada com sucesso');
            }
        } catch (error) {
            setErrorDialog(error instanceof Error ? error.message : intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.createTripError' }));
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Dialog
                show={props.show}
                onClose={props.onClose}
                showCloseButton
                bsSize={Dialog.SIZE_SM}
            >
                <Dialog.Title title={intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.title' })} />
                <Dialog.Body>
                    <div className="w-full">
                        <div className="text-gray-400 mb-8">
                            {intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.description' })}
                        </div>

                        <div className={`flex justify-between w-full gap-6 flex-wrap`}>
                            <div className={`form-group form-group-lg w-full`}>
                                <label htmlFor="assetId" className="truncate">{intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.searchVehicle' })}</label>
                                <SearchAutocomplete
                                    id="assetId"
                                    placeholder={intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.searchVehiclePlaceholder' })}
                                    fetchOptions={async (searchTerm) => {
                                        const response = getFilteredAssets(searchTerm, 50, 0);
                                        return response;
                                    }}
                                    optionLabel={{ name: intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.optionName' }), chassis: intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.optionChassis' }) }}
                                    onSelectedOption={(asset: Asset) => {
                                        setForm({
                                            ...form,
                                            assetId: asset.id,
                                            assetName: asset.name,
                                        });
                                    }}
                                />
                            </div>
                        </div>

                        <div className={`form-group form-group-lg w-full`}>
                            <label htmlFor="driverId" className="truncate">{intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.driver' })}</label>
                            <Select
                                id="driverId"
                                useFilter
                                options={props.drivers.map(driver => ({
                                    id: driver.id ?? '',
                                    label: driver.name,
                                    profileId: driver.profileId ?? '',
                                }))}
                                value={[form.driverId]}
                                onChange={(e) => {
                                    setForm({
                                        ...form,
                                        driverId: e?.id ?? '',
                                        driverName: e?.label ?? '',
                                        profileId: e?.profileId ?? '',
                                    });
                                }}
                            />
                        </div>
                    </div>
                </Dialog.Body>

                <Dialog.Footer>
                    <button 
                        className={`btn btn-primary ${loading ? 'btn-loading' : ''}`} 
                        onClick={handleCreateTrip} 
                        disabled={loading || !form.assetId || !form.driverId}
                    >
                        {loading ? intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.creating' }) : intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.createTrip' })} 
                    </button>
                </Dialog.Footer>
            </Dialog>
            
            <ErrorDialog
                show={!!errorDialog}
                onClose={() => setErrorDialog(null)}
                title={intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.errorTitle' })}
                body={errorDialog}
            />

            <SuccessDialog
                show={!!successDialog}
                onClose={() => {
                    setSuccessDialog(null);
                    props.onTripCreated(form.driverName);
                    props.onClose();
                }}
                title={intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.successTitle' })}
                body={intl.formatMessage({ id: 'intl-msg:kanban.form.createTripDialog.successBody' })}
            />
        </>
    );
};

export default CreateTripDialog;